import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import LeadsPage from '@/pages/LeadsPage';
import ProjectsPage from '@/pages/ProjectsPage';
import BookingsPage from '@/pages/BookingsPage';
import PaymentsPage from '@/pages/PaymentsPage';
import SettingsPage from '@/pages/SettingsPage';
import WhatsAppPage from '@/pages/WhatsAppPage';
import PortalLeadsPage from '@/pages/PortalLeadsPage';
import SiteVisitsPage from '@/pages/SiteVisitsPage';
import AttendancePage from '@/pages/AttendancePage';
import '@/App.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, refetchOnWindowFocus: false, retry: 1 } },
});

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading SalesOS...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/site-visits" element={<SiteVisitsPage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/payments" element={<ProtectedRoute roles={['director', 'finance', 'manager']}><PaymentsPage /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<WhatsAppPage />} />
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/portal-leads" element={<ProtectedRoute roles={['director', 'manager']}><PortalLeadsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute roles={['director']}><SettingsPage /></ProtectedRoute>} />
      </Route>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
